"use client";

import {NeoModal} from "@/components/@modal/neo.modal";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  DropdownTrigger
} from "@nextui-org/react";
import {useState} from "react";
import {FiChevronDown, FiChevronRight, FiClock, FiMapPin} from "react-icons/fi";

type SosCurrentTab = "active" | "resolved" | "falseAlarm" | "cancelled";

export type SosUser = {
  id: string;
  name: string;
  bookingId: string;
  avatar: string;
  phone: string;
  boardingPoint: string;
  dropPoint: string;
  reason: string;
  comment?: string;
  travelFrom: string;
  travelTo: string;
  travelDate: string;
  totalTravelTime: string;
};

export type SosCardItem = {
  id: string;
  busNo: string;
  pickupTime: string;
  caretakerName: string;
  driverCode: string;
  driverPh: string;
  from: string;
  to: string;
  status: 1 | 2 | 3 | 4;
  users: SosUser[];
};

const getActionButtonConfig = (tab: SosCurrentTab) => {
  switch (tab) {
    case "resolved":
      return {
        label: "Resolved",
        className:
          "h-9 min-w-[132px] rounded-xl bg-green-500 px-3 text-xs font-semibold text-white shadow-none hover:opacity-95",
        items: [
          {key: "falseAlarm", label: "False Alarm"},
          {key: "cancelled", label: "Cancelled"}
        ]
      };
    case "falseAlarm":
      return {
        label: "False Alarm",
        className:
          "h-9 min-w-[132px] rounded-xl bg-slate-500 px-3 text-xs font-semibold text-white shadow-none hover:opacity-95",
        items: [{key: "resolved", label: "Resolved"}]
      };
    case "cancelled":
      return {
        label: "Cancelled",
        className:
          "h-9 min-w-[132px] rounded-xl bg-black px-3 text-xs font-semibold text-white shadow-none hover:opacity-95",
        items: [{key: "resolved", label: "Resolved"}]
      };
    default:
      return {
        label: "Take Action",
        className:
          "h-9 min-w-[132px] rounded-xl bg-rose-500 px-3 text-xs font-semibold text-white shadow-none hover:opacity-95",
        items: [
          {key: "resolved", label: "Resolved"},
          {key: "falseAlarm", label: "False Alarm"},
          {key: "cancelled", label: "Cancelled"}
        ]
      };
  }
};

type PendingStatusChange = {
  userId: string;
  userName: string;
  bookingId: string;
  nextStatus: 1 | 2 | 3 | 4;
  label: string;
};

export const SosCommentConfirmModal = ({
  userName,
  bookingId,
  label,
  onConfirm,
  onClose
}: {
  userName: string;
  bookingId: string;
  label: string;
  onConfirm: (comment: string) => void;
  onClose: () => void;
}) => {
  const [comment, setComment] = useState("");
  const isValid = comment.trim().length >= 6;

  return (
    <div className="p-5">
      <div className="font-semibold text-slate-900">
        Mark {userName} ({bookingId}) as {label}?
      </div>
      <div className="mt-1 text-xs text-slate-500">
        Please add a comment before confirming this change.
      </div>

      <textarea
        className="mt-3 h-24 w-full resize-none rounded-xl border border-slate-200 p-2.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-gray-600"
        placeholder="Enter a comment..."
        value={comment}
        onChange={event => setComment(event.target.value)}
        autoFocus
      />
      <div className="mt-1 text-[11px] text-slate-400">
        Minimum 6 characters required
      </div>

      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white transition-colors enabled:hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
          disabled={!isValid}
          onClick={() => {
            if (!isValid) {
              return;
            }
            onConfirm(comment.trim());
            onClose();
          }}
        >
          Confirm
        </button>
      </div>
    </div>
  );
};

export const SOSDetailView = ({
  card,
  currentTab = "active",
  onStatusChange,
  onClose: _onClose
}: {
  card: SosCardItem;
  currentTab?: SosCurrentTab;
  onStatusChange: (
    cardId: string,
    userId: string,
    nextStatus: 1 | 2 | 3 | 4,
    comment?: string
  ) => void;
  onClose?: () => void;
}) => {
  const actionButton = getActionButtonConfig(currentTab);
  const firstUser = card.users[0];
  const routeName = `Route ${Number(card.users[0]?.bookingId ?? 0) % 12 || 4}`;
  const visibleUsers = card.users;
  const [pendingChange, setPendingChange] =
    useState<PendingStatusChange | null>(null);

  const applyStatusChange = (
    userId: string,
    userName: string,
    bookingId: string,
    nextStatus: 1 | 2 | 3 | 4,
    label: string
  ) => {
    setPendingChange({userId, userName, bookingId, nextStatus, label});
  };

  const handleConfirmWithComment = (comment: string) => {
    if (!pendingChange) {
      return;
    }
    onStatusChange(
      card.id,
      pendingChange.userId,
      pendingChange.nextStatus,
      comment
    );
    setPendingChange(null);
  };

  return (
    <div className="flex min-h-[calc(100vh-63px)] flex-col px-2 py-2">
      <div className=" pb-2">
        <div className=" font-semibold ">SOS Details</div>
      </div>

      <div className="mt-2 w-fit rounded-2xl border border-slate-200 bg-gray-50  p-4">
        <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
          <div className="shrink-0 border-r  border-slate-200 pr-4">
            <div className="text-xs font-medium text-slate-500">Bus</div>
            <div className="mt-1 whitespace-nowrap text-sm font-semibold text-slate-800">
              {card.busNo} / {routeName}
            </div>
          </div>

          <div className="shrink-0 border-r border-slate-200 pr-4">
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
              <FiClock className="h-3.5 w-3.5" />
              <span>Trip Timing</span>
            </div>
            <div className="mt-1 whitespace-nowrap text-sm font-semibold text-slate-800">
              {firstUser
                ? `${firstUser.travelFrom} - ${firstUser.travelTo}`
                : "-"}
            </div>
          </div>

          <div className="shrink-0">
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
              <FiMapPin className="h-3.5 w-3.5" />
              <span>Route</span>
            </div>
            <div className="mt-1 whitespace-nowrap text-sm font-semibold text-slate-800">
              {card.from} {"->"} {card.to}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-xs font-semibold">User Actions</div>
          </div>
          <div className="text-xs font-medium text-slate-600">
            Total users: {visibleUsers.length}
          </div>
        </div>

        <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
          {visibleUsers.length > 0 ? (
            <>
              <div className="hidden grid-cols-[1fr_0.8fr_1.05fr_1.05fr_1.35fr_1.35fr_0.9fr_0.9fr_0.95fr] gap-3 bg-slate-50 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 md:grid">
                <div>User</div>
                <div>Booking ID</div>
                <div>Boarding Point</div>
                <div>Drop Point</div>
                <div>Reason</div>
                <div>Comment</div>
                <div>Travel</div>
                <div>User No</div>
                <div>Action</div>
              </div>

              {visibleUsers.map(user => (
                <div
                  key={user.id}
                  className="grid gap-2.5 border-t border-slate-200 px-3 py-3 first:border-t-0 md:grid-cols-[1fr_0.8fr_1.05fr_1.05fr_1.35fr_1.35fr_0.9fr_0.9fr_0.95fr] md:items-center"
                >
                  <div className="text-xs font-semibold text-slate-800">
                    {user.name}
                  </div>
                  <div className="text-xs text-slate-600">{user.bookingId}</div>
                  <div className="text-xs text-slate-600">
                    {user.boardingPoint}
                  </div>
                  <div className="text-xs text-slate-600">{user.dropPoint}</div>
                  <div className="text-xs text-slate-600">{user.reason}</div>
                  <div className="text-xs text-slate-600">
                    {user.comment || <span className="text-slate-400">-</span>}
                  </div>
                  <div className="text-xs text-slate-600">
                    <div className="font-medium text-slate-700">
                      {user.travelFrom} - {user.travelTo}
                    </div>
                    <div className="mt-0.5 text-[11px] text-slate-500">
                      {user.travelDate} | {user.totalTravelTime}
                    </div>
                  </div>
                  <div className="text-xs text-slate-600">{user.phone}</div>
                  <div>
                    <Dropdown>
                      <DropdownTrigger>
                        <Button size="sm" className={actionButton.className}>
                          <span>{actionButton.label}</span>
                          <FiChevronDown className="h-3.5 w-3.5 opacity-90" />
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu
                        aria-label="Rider actions"
                        onAction={key => {
                          const item = actionButton.items.find(
                            option => option.key === key
                          );
                          if (!item) {
                            return;
                          }
                          const nextStatus =
                            key === "resolved"
                              ? 2
                              : key === "falseAlarm"
                                ? 3
                                : key === "cancelled"
                                  ? 4
                                  : null;
                          if (!nextStatus) {
                            return;
                          }
                          applyStatusChange(
                            user.id,
                            user.name,
                            user.bookingId,
                            nextStatus,
                            item.label
                          );
                        }}
                      >
                        <DropdownSection title="Status" showDivider>
                          {actionButton.items.map(item => (
                            <DropdownItem
                              key={item.key}
                              endContent={
                                <FiChevronRight className="h-4 w-4 text-slate-700" />
                              }
                            >
                              {item.label}
                            </DropdownItem>
                          ))}
                        </DropdownSection>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="px-4 py-8 text-center text-xs text-slate-500">
              No users matched your filters.
            </div>
          )}
        </div>
      </div>

      <NeoModal
        actionType="sosCommentConfirm"
        onOpen={!!pendingChange}
        onClose={() => setPendingChange(null)}
        hasActions={false}
        data={
          pendingChange
            ? {
                userName: pendingChange.userName,
                bookingId: pendingChange.bookingId,
                label: pendingChange.label,
                onConfirm: handleConfirmWithComment
              }
            : undefined
        }
      />
    </div>
  );
};
